/**
* NavHeader — 48px bar below the main header.
*
* Current scope (workspace group only, per Colin 2026-04-18):
*   • Left: waffle icon + tab row (one fixed "Focus" tab + removable
*     "New Chat" tabs created via the `+` dropdown).
*   • Right utility row: Context (category), Reasoning (robot_2),
*     Activity Log (auto_stories), Notifications (bell + badge), AI spark.
*
* Panel wiring:
*   • Notifications bell → existing NotificationPanel (AppShell state).
*   • AI spark           → existing AIPanel (AppShell state).
*   • Context / Reasoning / Activity Log → `activeUtilityTab` state lifted
*     into AppShell. The utility panel itself lands in Phase 8; here we
*     only drive the `--active` styling on these three buttons.
*
* Tabs are stored in local state because this is the only consumer for
* now. When more tab groups arrive we'll promote to AppStore.
*/

import React, { useCallback, useMemo, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Tooltip } from '../Tooltip';
import {
  TabAddMenu,
  type TabAddAction,
  type RecentTabEntry,
  type RecentTabActionId,
  type RecentTabActionPayload,
  type RecentTabActionResult,
  type ShareRecipientSearchResult,
} from './TabAddMenu';

interface KCiEventBusLike {
  publish<T>(type: string, payload: T, options: { source: string; target?: string | string[] }): void;
}

interface TruncatedTooltipLabelProps {
  label: string;
  tooltipTarget: string;
  className: string;
  truncationOffset?: number;
}

function TruncatedTooltipLabel({
  label,
  tooltipTarget,
  className,
  truncationOffset = 2,
}: TruncatedTooltipLabelProps) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    const check = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth + truncationOffset);
    };

    requestAnimationFrame(check);

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => requestAnimationFrame(check)) : null;
    observer?.observe(el);

    return () => observer?.disconnect();
  }, [label, truncationOffset]);

  return (
    <>
      <span
        ref={spanRef}
        className={className}
        data-target={isTruncated ? tooltipTarget : undefined}
      >
        {label}
      </span>
      {isTruncated && (
        <Tooltip
          tooltipTarget={tooltipTarget}
          tooltipText={label}
          arrow={false}
          arrowPosition="Bottom"
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// TruncatedTabLabel — shows the Tooltip only when the label is actually
// clipped. ResizeObserver keeps truncation state accurate after every layout.
// ---------------------------------------------------------------------------
function TruncatedTabLabel({ label, tabId }: { label: string; tabId: string }) {
  const tooltipTarget = `tab-label-${tabId}`;

  return (
    <TruncatedTooltipLabel
      label={label}
      tooltipTarget={tooltipTarget}
      className="nav-header__tab-label"
    />
  );
}

// ---------------------------------------------------------------------------
// TruncatedGroupPillLabel — mirrors tab tooltip behavior for client group
// pills, showing Tooltip only when the text is actually clipped.
// ---------------------------------------------------------------------------
function TruncatedGroupPillLabel({ label, groupId }: { label: string; groupId: string }) {
  const tooltipTarget = `group-pill-label-${groupId}`;

  return (
    <TruncatedTooltipLabel
      label={label}
      tooltipTarget={tooltipTarget}
      className="nav-header__group-pill-label"
    />
  );
}

export type UtilityTab = 'context' | 'reasoning' | 'activity' | null;
export type NavDockKind = 'chats' | 'workspaces' | 'engagements' | 'agents' | null;

/**
* Tab role per Session 56 / 57 spec — lower roles sort first within an
* engagement sub-block. Role 0 = Engagement Dashboard, Role 1 = Canvas,
* Role 2 = Team Chat, Role 3 = Substantive Procedure parent, Roles 4-6 =
* SP children (Data Extraction / Document Mapping / Review Work Paper),
* Role 7 = Risk Assessment, Role 9 = anything else (saved chats, ad-hoc).
*/
export type TabRole = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 9;

export interface ChatTabContext {
  kind: 'chat';
  sessionId: string | null;
  title: string;
  agentId?: string | null;
  sourceTabId?: string;
  workspaceId?: string;
  objectiveId?: string;
}

export interface WorkspaceTabContext {
  kind: 'workspace';
  workspaceId: string;
  objectiveId: string | null;
  pageId: string;
  objectiveType?: string;
  objectiveCategory?: string;
  contentType?: string;
  storageKey?: string;
}

export interface AgentTabContext {
  kind: 'agent';
  agentId: string | null;
  pageId: string;
  agentName: string;
  isGlobal?: boolean;
}

export interface EngagementTabContext {
  kind: 'engagement';
  engagementId: string;
  clientId?: string | null;
  pageId: string;
}

export type NavTabContext =
  | ChatTabContext
  | WorkspaceTabContext
  | AgentTabContext
  | EngagementTabContext;

export interface NavTab {
  /** Stable id (used as React key and active-tab marker). */
  id: string;
  label: string;
  /**
   * Optional i18n key — when set, the renderer looks it up via `t()`
   * at render time so language switches re-translate the label. Falls
   * back to `label` if the key is missing from the dict.
   */
  labelKey?: string;
  /** Fixed tabs render without a close X. */
  removable: boolean;
  /** Optional client/engagement grouping. Tabs sharing a clientId cluster. */
  clientId?: string | null;
  /** Optional engagement id (a tab can be engagement-scoped within a client). */
  engId?: string | null;
  /** Optional parent tab whose dependent tab should render immediately after it. */
  parentTabId?: string | null;
  /** Role within the engagement sub-block — drives ordering. */
  role?: TabRole;
  /** Human-readable client label (rendered as the group pill). */
  clientLabel?: string;
  /** Optional mapping for host-level dock highlight synchronization. */
  tabKind?: NavDockKind;
  /** Optional ownership/publish metadata used by Recent Tabs action gating. */
  isOwnedByCurrentUser?: boolean;
  isCustomAgent?: boolean;
  isPublishedCustomAgent?: boolean;
  memberFirmCode?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  isGlobalAgent?: boolean;
  /** Tab-kind-specific data used to restore content when the tab is activated. */
  context?: NavTabContext;
}

export const NAV_FOCUS_TAB_ID = '__focus__';
const BLANK_CHAT_LABEL_KEY = 'tab.newChat';
const MRU_HISTORY_SIZE = 5;
const RECENT_TABS_LIMIT = 10;

const INITIAL_RECENT_TABS: RecentTabEntry[] = [
  { id: 'recent-timeline-change', label: 'Timeline Change', tabKind: 'chats', timestamp: Date.now() - 3600000 },
  { id: 'recent-substantive-proc', label: 'Substantive Procedure', tabKind: 'workspaces', clientLabel: 'AlphaGo FY28 > Substantive Proced...', engId: 'eng-alphago', timestamp: Date.now() - 7200000 },
  { id: 'recent-bank-recon', label: 'Bank Recon Agent', tabKind: 'agents', timestamp: Date.now() - 10800000 },
  { id: 'recent-risk-assessment', label: 'Risk Assessment', tabKind: 'workspaces', clientLabel: 'GammaStar FY28 > Risk Assessment', engId: 'eng-gammastar', timestamp: Date.now() - 14400000 },
  { id: 'recent-fixed-assets-chat', label: 'Fixed Assets Depreciation Chat', tabKind: 'chats', timestamp: Date.now() - 18000000 },
  { id: 'recent-revenue-qa', label: 'Revenue Recognition Q&A Chat', tabKind: 'chats', timestamp: Date.now() - 86400000 },
];

const INITIAL_TABS: NavTab[] = [
  { id: NAV_FOCUS_TAB_ID, label: 'Focus', labelKey: 'nav.focus', removable: false, tabKind: 'chats' },
];

function applyRecentTabPinState(entry: RecentTabEntry, shouldPin: boolean): RecentTabEntry {
  return {
    ...entry,
    isPinned: shouldPin,
    pinnedAt: shouldPin ? Date.now() : null,
  };
}

function handleNavTabRowWheel(event: React.WheelEvent<HTMLDivElement>): void {
  const tabRow = event.currentTarget;
  const maxScrollLeft = tabRow.scrollWidth - tabRow.clientWidth;
  const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;

  if (maxScrollLeft <= 0 || delta === 0) {
    return;
  }

  const nextScrollLeft = Math.min(
    Math.max(tabRow.scrollLeft + delta, 0),
    maxScrollLeft,
  );
  if (nextScrollLeft === tabRow.scrollLeft) {
    return;
  }

  event.preventDefault();
  tabRow.scrollLeft = nextScrollLeft;
}

export function scrollTabIntoView(
  tabRow: HTMLElement,
  tab: HTMLElement,
  stickyTrailingElement?: HTMLElement | null,
): void {
  const rowRect = tabRow.getBoundingClientRect();
  const tabRect = tab.getBoundingClientRect();
  const trailingRect = stickyTrailingElement?.getBoundingClientRect();
  const visibleRight = trailingRect
    ? Math.min(rowRect.right, trailingRect.left)
    : rowRect.right;

  if (tabRect.right > visibleRight) {
    tabRow.scrollLeft += tabRect.right - visibleRight;
  } else if (tabRect.left < rowRect.left) {
    tabRow.scrollLeft -= rowRect.left - tabRect.left;
  }
}

/**
* Group tabs by clientId, preserving first-seen overall order. Tabs with no
* clientId render as ungrouped slots and may be interleaved with client groups.
* Within each client group, engagements cluster by first-seen engId; within an
* engagement sub-block, tabs sort by `role` (Dashboard → Canvas → Team
* Chat → SP → SP children → Risk → other).
*
* Pure — no side effects on input objects. (Prior implementation tagged
* each tab with a `__idx` field to enable stable secondary sort; that was
* mutation-as-storage and would break consumers that rely on a frozen
* input. Index tracking now uses a parallel `WeakMap<NavTab, number>`.)
*/
export function groupTabs(tabs: NavTab[]): Array<{
  clientId: string | null;
  clientLabel: string | null;
  tabs: NavTab[];
}> {
  const orderSlots: Array<{ type: 'ungrouped'; tab: NavTab } | { type: 'group'; clientId: string }> = [];
  const groupMap = new Map<string, { label: string; tabs: NavTab[] }>();

  for (const tab of tabs) {
    if (!tab.clientId) {
      orderSlots.push({ type: 'ungrouped', tab });
      continue;
    }
    if (!groupMap.has(tab.clientId)) {
      groupMap.set(tab.clientId, { label: tab.clientLabel ?? tab.clientId, tabs: [] });
      orderSlots.push({ type: 'group', clientId: tab.clientId });
    }
    groupMap.get(tab.clientId)!.tabs.push(tab);
  }

  // Within each client group, cluster by engId in first-seen order, then
  // sort each engagement sub-block by role (stable secondary by index).
  // The WeakMap is scoped to this call; it doesn't touch the input tabs.
  for (const clientId of groupMap.keys()) {
    const group = groupMap.get(clientId)!;
    const engOrder: Array<string | null> = [];
    const engBuckets = new Map<string | null, NavTab[]>();
    const originalIndex = new WeakMap<NavTab, number>();

    group.tabs.forEach((tab, i) => {
      originalIndex.set(tab, i);
      const eng = tab.engId ?? null;
      if (!engBuckets.has(eng)) {
        engBuckets.set(eng, []);
        engOrder.push(eng);
      }
      engBuckets.get(eng)!.push(tab);
    });

    const sorted: NavTab[] = [];
    for (const eng of engOrder) {
      const bucket = engBuckets.get(eng)!;
      const getParentTabId = (tab: NavTab) =>
        tab.parentTabId ??
        (tab.context?.kind === 'chat' ? tab.context.sourceTabId : undefined);
      bucket.sort((a, b) => {
        const ra = a.role ?? 9;
        const rb = b.role ?? 9;
        if (ra !== rb) return ra - rb;
        const ai = originalIndex.get(a) ?? 0;
        const bi = originalIndex.get(b) ?? 0;
        return ai - bi;
      });
      const bucketIds = new Set(bucket.map((tab) => tab.id));
      const childrenByParent = new Map<string, NavTab[]>();
      for (const tab of bucket) {
        const parentTabId = getParentTabId(tab);
        if (!parentTabId || !bucketIds.has(parentTabId) || parentTabId === tab.id) {
          continue;
        }
        const children = childrenByParent.get(parentTabId) ?? [];
        children.push(tab);
        childrenByParent.set(parentTabId, children);
      }

      const visited = new Set<string>();
      const appendWithChildren = (tab: NavTab) => {
        if (visited.has(tab.id)) return;
        visited.add(tab.id);
        sorted.push(tab);
        childrenByParent.get(tab.id)?.forEach(appendWithChildren);
      };

      bucket
        .filter((tab) => {
          const parentTabId = getParentTabId(tab);
          return !parentTabId || !bucketIds.has(parentTabId);
        })
        .forEach(appendWithChildren);
      bucket.forEach(appendWithChildren);
    }
    group.tabs = sorted;
  }

  const result: Array<{
    clientId: string | null;
    clientLabel: string | null;
    tabs: NavTab[];
  }> = [];
  for (const slot of orderSlots) {
    if (slot.type === 'ungrouped') {
      result.push({ clientId: null, clientLabel: null, tabs: [slot.tab] });
      continue;
    }
    const group = groupMap.get(slot.clientId);
    if (!group) {
      continue;
    }
    result.push({ clientId: slot.clientId, clientLabel: group.label, tabs: group.tabs });
  }
  return result;
}

export interface NavHeaderProps {
  /** Translation function supplied by the host app. */
  t: (key: string) => string;
  /** Open notification panel (wired to AppShell state). */
  onToggleNotifications: () => void;
  /** Open AI panel (wired to AppShell state). */
  onToggleAi: () => void;
  /** Close the open AI panel. */
  onCloseAi?: () => void;
  notifPanelOpen: boolean;
  aiPanelOpen: boolean;
  /** Whether the docked Ask KCi panel is collapsed. */
  hideAiSpark?: boolean;
  /** Unread notification count. Badge hidden when 0. */
  notifUnread?: number;
  /** Active utility tab (Context / Reasoning / Activity). Null = none. */
  activeUtilityTab: UtilityTab;
  onUtilityTabChange: (tab: UtilityTab) => void;
  /** Toggle the workspace assets panel. */
  onToggleAssets?: () => void;
  /** Whether the assets panel is currently open. */
  assetsPanelOpen?: boolean;
  /** Toggle the milestones panel in the active workspace screen. */
  onToggleMilestone?: () => void;
  /** Whether the milestones panel is currently open. */
  milestonePanelOpen?: boolean;
  /** Optional tab change callback for host-only UI synchronization. */
  onActiveTabChange?: (tab: NavTab | null) => void;
  /** Controlled tabs from host (Option A integration). */
  tabs?: NavTab[];
  /** Controlled active tab id from host. */
  activeTabId?: string;
  /** Controlled recent tabs list from host. */
  recentTabs?: RecentTabEntry[];
  /** Controlled set of collapsed group ids from host. */
  collapsedGroupIds?: string[];
  /** Controlled callback: activate/focus a tab, including its attached context. */
  onTabActivate?: (
    tabId: string,
    options?: { trackFocus?: boolean },
    tab?: NavTab,
  ) => void;
  /** Controlled callback: close tab by id. */
  onTabClose?: (tabId: string) => void;
  /** Controlled callback: execute add-menu action. */
  onTabAddAction?: (action: TabAddAction) => void;
  /** Optional callback fired after the + menu is opened and rendered. */
  onAddMenuOpened?: () => void;
  /** Controlled callback: reopen/activate a recent tab entry. */
  onRecentTabOpen?: (entry: RecentTabEntry) => void;
  /** Controlled callback: pin or unpin a recent tab entry. */
  onRecentTabPinToggle?: (entry: RecentTabEntry, shouldPin: boolean) => void;
  /** Controlled callback: execute a recent-tab action (rename/share/edit/download/delete). */
  onRecentTabAction?: (
    entry: RecentTabEntry,
    actionId: RecentTabActionId,
    payload?: RecentTabActionPayload,
  ) => RecentTabActionResult | Promise<RecentTabActionResult>;
  /** Optional Graph-backed directory lookup for agent-share recipients. */
  searchShareRecipients?: (query: string) => Promise<ShareRecipientSearchResult[]>;
  /** Optional loader for users who already have an active share for the agent. */
  loadSharedRecipientIds?: (entry: RecentTabEntry) => Promise<string[]>;
  /** Feature flag (applicationSettings.Agents.allowAgentSharing) that gates the recent-tab Share action. */
  allowAgentSharing?: boolean;
  /** Controlled callback: toggle a workspace group collapse state. */
  onToggleGroupCollapse?: (groupId: string) => void;
}

export const NavHeader = React.memo(function NavHeader({
  t,
  onToggleNotifications: _onToggleNotifications,
  onToggleAi,
  onCloseAi,
  hideAiSpark = false,
  notifPanelOpen: _notifPanelOpen,
  aiPanelOpen,
  notifUnread: _notifUnread = 5,
  activeUtilityTab,
  onUtilityTabChange,
  onToggleAssets,
  assetsPanelOpen = false,
  onToggleMilestone,
  milestonePanelOpen = false,
  onActiveTabChange,
  tabs: controlledTabs,
  activeTabId: controlledActiveTabId,
  recentTabs: controlledRecentTabs,
  collapsedGroupIds: controlledCollapsedGroupIds,
  onTabActivate,
  onTabClose,
  onTabAddAction,
  onAddMenuOpened,
  onRecentTabOpen,
  onRecentTabPinToggle,
  onRecentTabAction,
  searchShareRecipients,
  loadSharedRecipientIds,
  allowAgentSharing = false,
  onToggleGroupCollapse,
}: NavHeaderProps) {
  const isTabsControlled = Array.isArray(controlledTabs);
  const isActiveTabControlled = typeof controlledActiveTabId === 'string';
  const isRecentTabsControlled = Array.isArray(controlledRecentTabs);
  const isCollapsedGroupsControlled = Array.isArray(controlledCollapsedGroupIds);

  const [internalTabs, setInternalTabs] = useState<NavTab[]>(INITIAL_TABS);
  const [internalActiveTabId, setInternalActiveTabId] = useState<string>(NAV_FOCUS_TAB_ID);
  const [internalRecentTabs, setInternalRecentTabs] = useState<RecentTabEntry[]>(INITIAL_RECENT_TABS);
  const [internalCollapsedGroupIds, setInternalCollapsedGroupIds] = useState<Record<string, boolean>>({});

  const tabs = isTabsControlled ? controlledTabs : internalTabs;
  const activeTabId = (isActiveTabControlled ? controlledActiveTabId : internalActiveTabId) ?? NAV_FOCUS_TAB_ID;
  const recentTabs = isRecentTabsControlled ? controlledRecentTabs : internalRecentTabs;
  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? null;
  const isAllAgentsActivity = activeTabId === 'agent:all-agents-activity';
  const isAgentBuilder = activeTabId === 'agent:agent-builder';
  const showAssetsButton =
    activeTab?.tabKind !== 'chats' &&
    !activeTabId.startsWith('ai-chat-') &&
    !isAllAgentsActivity &&
    !isAgentBuilder;

  const collapsedGroupIds = useMemo(() => {
    if (isCollapsedGroupsControlled) {
      return new Set(controlledCollapsedGroupIds);
    }
    return new Set(
      Object.entries(internalCollapsedGroupIds)
        .filter(([, isCollapsed]) => isCollapsed)
        .map(([groupId]) => groupId)
    );
  }, [controlledCollapsedGroupIds, internalCollapsedGroupIds, isCollapsedGroupsControlled]);

  const mruTabIdsRef = useRef<string[]>([]);
  // Ref mirror of `tabs` so callbacks can read current tab data without
  // stale closures and without calling setTabs as a read-trick (anti-pattern).
  const tabsRef = useRef<NavTab[]>(INITIAL_TABS);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addMenuPos, setAddMenuPos] = useState<{ top: number; left: number } | null>(null);
  const tabRowRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef(new Map<string, HTMLDivElement>());

  // Derived grouping — recomputed when tabs change. Pure transform, no side
  // effects on the tab list itself.
  const tabGroups = useMemo(() => groupTabs(tabs), [tabs]);

  // Keep ref in sync with state so callbacks always see the latest tabs.
  useLayoutEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  const addBtnRef = useRef<HTMLButtonElement | null>(null);
  const chatCounterRef = useRef(0);

  const focusTabById = useCallback((id: string) => {
    const tabEl = tabRefs.current.get(id);
    tabEl?.focus();
  }, []);

  const getVisibleTabsForGroup = useCallback(
    (group: { clientId: string | null; tabs: NavTab[] }) => {
      if (!group.clientId) {
        return group.tabs;
      }
      if (!collapsedGroupIds.has(group.clientId)) {
        return group.tabs;
      }
      const activeTab = group.tabs.find((tab) => tab.id === activeTabId);
      return activeTab ? [activeTab] : [];
    },
    [activeTabId, collapsedGroupIds]
  );

  const activateTab = useCallback((id: string, options?: { trackFocus?: boolean }) => {
    if (onTabActivate) {
      onTabActivate(id, options, tabsRef.current.find((tab) => tab.id === id));
      return;
    }

    setInternalActiveTabId(id);
    // The focus (home) tab is never tracked in MRU or recent tabs unless
    // the caller explicitly opts in via trackFocus.
    if (id === NAV_FOCUS_TAB_ID && !options?.trackFocus) return;
    const nextMru = [id, ...mruTabIdsRef.current.filter(tabId => tabId !== id)].slice(
      0,
      MRU_HISTORY_SIZE
    );
    mruTabIdsRef.current = nextMru;
    // Read current tabs from ref — avoids stale closure without the
    // anti-pattern of calling setTabs purely as a state-read vehicle.
    const tab = tabsRef.current.find(t => t.id === id);
    if (!tab || tab.id === NAV_FOCUS_TAB_ID) return;
    const entry: RecentTabEntry = {
      id: tab.id,
      label: tab.label,
      clientLabel: tab.clientLabel,
      groupId: tab.clientId ?? null,
      engId: tab.engId,
      timestamp: Date.now(),
      tabKind: tab.tabKind ?? null,
    };
    setInternalRecentTabs(prev => {
      const existing = prev.find((item) => item.id === tab.id);
      const nextEntry: RecentTabEntry = {
        ...entry,
        isPinned: existing?.isPinned ?? false,
        pinnedAt: existing?.pinnedAt ?? null,
      };
      const filtered = prev.filter(r => r.id !== tab.id);
      return [nextEntry, ...filtered].slice(0, RECENT_TABS_LIMIT);
    });
  }, [onTabActivate]);

  const openAddMenu = useCallback(() => {
    const btn = addBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    // Anchor menu below the + with a small gap; shift left so the 280px menu
    // stays on-screen. Mirrors the prototype's tab-add-menu positioning.
    const top = rect.bottom + 4;
    let left = rect.left + rect.width / 2 - 140; // 280/2
    const maxLeft = window.innerWidth - 280 - 8;
    if (left > maxLeft) left = maxLeft;
    if (left < 8) left = 8;
    setAddMenuPos({ top, left });
    setAddMenuOpen(true);
  }, []);

  const closeAddMenu = useCallback(() => setAddMenuOpen(false), []);

  useEffect(() => {
    if (!addMenuOpen) {
      return;
    }

    onAddMenuOpened?.();
  }, [addMenuOpen, onAddMenuOpened]);

  const handleAddAction = useCallback((action: TabAddAction) => {
    if (onTabAddAction) {
      onTabAddAction(action);
      return;
    }

    if (action === 'new-chat') {
      const existingBlankChat = tabs.find(
        (tab) =>
          tab.labelKey === 'nav.focus' || tab.labelKey === BLANK_CHAT_LABEL_KEY
      );
      if (existingBlankChat) {
        activateTab(existingBlankChat.id, { trackFocus: true });
        return;
      }
      chatCounterRef.current += 1;
      const newId = `chat-${Date.now()}-${chatCounterRef.current}`;
      const newTab: NavTab = {
        id: newId,
        // Label keyed off i18n so language switches re-translate.
        label: `${t('tab.newChat')}`,
        labelKey: BLANK_CHAT_LABEL_KEY,
        removable: true,
        tabKind: 'chats',
      };
      setInternalTabs(prev => [...prev, newTab]);
      // Defer one frame so useLayoutEffect can sync tabsRef before
      // activateTab tries to read the new tab for recent tracking.
      requestAnimationFrame(() => activateTab(newId));
      return;
    }
    if (action === 'close-all') {
      // Keep Focus tab and drop all removables. Recent history is retained
      // so closed tabs remain available under "Recent Tabs" for reopening.
      setInternalTabs(prev => {
        return prev.filter(tb => !tb.removable);
      });
      mruTabIdsRef.current = [];
      setInternalActiveTabId(NAV_FOCUS_TAB_ID);
      return;
    }
    if (action === 'new-workspace') {
      // Demo wiring — spawns a workspace tab against the current engagement
      // context (mocked as AlphaGo for now; clara-core wires the real one).
      chatCounterRef.current += 1;
      const newId = `ws-new-${Date.now()}-${chatCounterRef.current}`;
      const newTab: NavTab = {
        id: newId,
        label: t('tab.newWorkspace'),
        labelKey: 'tab.newWorkspace',
        removable: true,
        clientId: 'eng-alphago',
        engId: 'eng-alphago',
        clientLabel: 'AlphaGo FY28',
        role: 9,
        tabKind: 'workspaces',
      };
      setInternalTabs(prev => [...prev, newTab]);
      requestAnimationFrame(() => activateTab(newId));
      return;
    }
    // new-engagement / new-agent — stubbed until those
    // tab groups and target pages exist in the React port.
  }, [t, activateTab, tabs, onTabAddAction]);

  const handleTabClick = useCallback((id: string) => {
    // Explicit user selection should count as recent usage, including Focus.
    activateTab(id, { trackFocus: true });
  }, [activateTab]);

  const handleGroupToggle = useCallback((groupId: string) => {
    if (onToggleGroupCollapse) {
      onToggleGroupCollapse(groupId);
      return;
    }

    setInternalCollapsedGroupIds((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }, [onToggleGroupCollapse]);

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent, id: string) => {
      const orderedIds = tabGroups.flatMap(group => getVisibleTabsForGroup(group).map(tab => tab.id));
      const currentIndex = orderedIds.indexOf(id);
      if (currentIndex < 0) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activateTab(id, { trackFocus: true });
        return;
      }

      let targetId: string | null = null;
      if (e.key === 'ArrowRight') {
        targetId = orderedIds[(currentIndex + 1) % orderedIds.length];
      } else if (e.key === 'ArrowLeft') {
        targetId = orderedIds[(currentIndex - 1 + orderedIds.length) % orderedIds.length];
      } else if (e.key === 'Home') {
        targetId = orderedIds[0];
      } else if (e.key === 'End') {
        targetId = orderedIds[orderedIds.length - 1];
      }

      if (!targetId || targetId === id) return;
      e.preventDefault();
      activateTab(targetId, { trackFocus: true });
      requestAnimationFrame(() => focusTabById(targetId!));
    },
    [activateTab, focusTabById, tabGroups, getVisibleTabsForGroup]
  );

  useEffect(() => {
    if (!onActiveTabChange) return;
    onActiveTabChange(tabs.find(tab => tab.id === activeTabId) ?? null);
  }, [activeTabId, onActiveTabChange, tabs]);

  useEffect(() => {
    const tabElement = tabRefs.current.get(activeTabId);
    const tabRow = tabRowRef.current;
    if (!tabElement || !tabRow) return;

    const frameId = requestAnimationFrame(() => {
      scrollTabIntoView(tabRow, tabElement, addBtnRef.current);
    });

    return () => cancelAnimationFrame(frameId);
  }, [activeTabId]);

  const handleTabClose = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();

      const closingTab = tabsRef.current.find((tab) => tab.id === id);
      if (!isRecentTabsControlled && closingTab && closingTab.id !== NAV_FOCUS_TAB_ID) {
        const entry: RecentTabEntry = {
          id: closingTab.id,
          label: closingTab.label,
          clientLabel: closingTab.clientLabel,
          groupId: closingTab.clientId ?? null,
          engId: closingTab.engId,
          timestamp: Date.now(),
          tabKind: closingTab.tabKind ?? null,
        };
        setInternalRecentTabs((prev) => {
          const existing = prev.find((item) => item.id === closingTab.id);
          const nextEntry: RecentTabEntry = {
            ...entry,
            isPinned: existing?.isPinned ?? false,
            pinnedAt: existing?.pinnedAt ?? null,
          };
          const filtered = prev.filter((r) => r.id !== closingTab.id);
          return [nextEntry, ...filtered].slice(0, RECENT_TABS_LIMIT);
        });
      }

      if (onTabClose) {
        onTabClose(id);
        return;
      }

      setInternalTabs(prev => {
        const exists = prev.some(t => t.id === id);
        if (!exists) return prev;
        const next = prev.filter(t => t.id !== id);
        const nextMru = mruTabIdsRef.current
          .filter(tabId => tabId !== id)
          .slice(0, MRU_HISTORY_SIZE);
        mruTabIdsRef.current = nextMru;
        // When the active tab closes, activate the most recently used tab.
        // Focus is a fallback only and is not part of the regular MRU list.
        if (activeTabId === id) {
          const fallbackId =
            nextMru.find(tabId => next.some(tab => tab.id === tabId)) ?? NAV_FOCUS_TAB_ID;
          if (fallbackId === NAV_FOCUS_TAB_ID) {
            setInternalActiveTabId(NAV_FOCUS_TAB_ID);
          } else {
            activateTab(fallbackId);
          }
        }
        return next;
      });
    },
    [activeTabId, activateTab, isRecentTabsControlled, onTabClose]
  );

  const handleRecentTabPinToggle = useCallback(
    (entry: RecentTabEntry, shouldPin: boolean) => {
      if (onRecentTabPinToggle) {
        onRecentTabPinToggle(entry, shouldPin);
        return;
      }

      if (isRecentTabsControlled) {
        return;
      }

      setInternalRecentTabs((prev) =>
        prev.map((item) =>
          item.id === entry.id ? applyRecentTabPinState(item, shouldPin) : item
        )
      );
    },
    [isRecentTabsControlled, onRecentTabPinToggle]
  );

  const handleRecentTabSelect = useCallback(
    (entry: RecentTabEntry) => {
      if (onRecentTabOpen) {
        onRecentTabOpen(entry);
        return;
      }

      const existing = tabsRef.current.find(t => t.id === entry.id);
      if (existing) {
        // Tab is already open — activate it
        activateTab(entry.id, { trackFocus: true });
        return;
      }
      // Tab was closed — reopen as a removable ungrouped tab
      const reopened: NavTab = {
        id: entry.id,
        label: entry.label,
        removable: true,
        clientId: entry.groupId ?? null,
        clientLabel: entry.clientLabel ?? undefined,
        engId: entry.engId ?? null,
        tabKind: entry.tabKind,
      };
      setInternalTabs(prev => [...prev, reopened]);
      // Defer so tabsRef syncs before activateTab reads it
      requestAnimationFrame(() => activateTab(entry.id, { trackFocus: true }));
    },
    [activateTab, onRecentTabOpen]
  );

  // Keep menu pinned to the + button if the window resizes while it's open.
  useLayoutEffect(() => {
    if (!addMenuOpen) return;
    const onResize = () => {
      const btn = addBtnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const top = rect.bottom + 4;
      let left = rect.left + rect.width / 2 - 140;
      const maxLeft = window.innerWidth - 280 - 8;
      if (left > maxLeft) left = maxLeft;
      if (left < 8) left = 8;
      setAddMenuPos({ top, left });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [addMenuOpen]);

  const handleUtilityClick = useCallback(
    (tab: Exclude<UtilityTab, null>) => {
      const nextTab = activeUtilityTab === tab ? null : tab;

      if (tab === 'context') {
        const eventPayload = {
          source: 'kci-core',
          tabKey: tab,
          selectedToolTab: '1',
          selectedPromptTab: '0',
          activeUtilityTab: nextTab,
          clickedAt: Date.now(),
        };

        console.info('[KCI-EventDebug] Context click -> window event', eventPayload);
        window.dispatchEvent(
          new CustomEvent('kci-context-button-clicked', {
            detail: eventPayload,
          })
        );

        const bus = (window as Window & { __KCI_EVENT_BUS__?: KCiEventBusLike }).__KCI_EVENT_BUS__;
        if (!bus) {
          console.warn('[KCI-EventDebug] Context click -> bus publish skipped (no __KCI_EVENT_BUS__)');
          return;
        }

        console.info('[KCI-EventDebug] Context click -> bus publish ContextButtonClicked', eventPayload);
        bus.publish(
          'ContextButtonClicked',
          eventPayload,
          {
            source: 'kci-core',
            target: 'clara-ai',
          }
        );
      }
    },
    [activeUtilityTab, onUtilityTabChange]
  );

  return (
    <nav className="nav-header">
      {/* Left: tab row */}
      <div ref={tabRowRef} className="nav-header__tab-row" onWheel={handleNavTabRowWheel}>

        <div className="nav-header__tabs" role="tablist" aria-label={t('nav.tabs')}>
          {tabGroups.map((group, groupIdx) => {
            // Each ungrouped tab renders inline; grouped tabs render with
            // a label pill in front of the group.
            const isGroup = group.clientId !== null;
            const isCollapsed = isGroup && collapsedGroupIds.has(group.clientId!);
            const activeInGroup = group.tabs.some((tab) => tab.id === activeTabId);
            const visibleTabs = getVisibleTabsForGroup(group);
            return (
              <React.Fragment key={group.clientId ?? `ungrouped-${groupIdx}`}>
                {isGroup && (
                  <button
                    type="button"
                    className={
                      'nav-header__group-pill' +
                      (isCollapsed && activeInGroup ? ' nav-header__group-pill--active' : '')
                    }
                    data-client-id={group.clientId}
                    onClick={() => handleGroupToggle(group.clientId!)}
                    aria-expanded={!isCollapsed}
                  >
                    <TruncatedGroupPillLabel
                      label={group.clientLabel ?? ''}
                      groupId={group.clientId ?? `group-${groupIdx}`}
                    />
                  </button>
                )}
                {visibleTabs.map(tab => {
                  const isActive = tab.id === activeTabId;
                  const cls =
                    'nav-header__tab' +
                    (isActive ? ' nav-header__tab--active' : '') +
                    (tab.removable ? ' nav-header__tab--removable' : '');
                  // Tabs with a labelKey re-translate on language change;
                  // freeform labels (e.g. "New Chat 3") render literally.
                  const displayLabel = tab.labelKey ? t(tab.labelKey) : tab.label;
                  return (
                    <div
                      key={tab.id}
                      className={cls}
                      role="tab"
                      tabIndex={isActive ? 0 : -1}
                      aria-selected={isActive}
                      ref={el => {
                        if (el) {
                          tabRefs.current.set(tab.id, el);
                        } else {
                          tabRefs.current.delete(tab.id);
                        }
                      }}
                      onClick={() => handleTabClick(tab.id)}
                      onKeyDown={e => handleTabKeyDown(e, tab.id)}
                    >
                      {tab.removable ? (
                        <span className="nav-header__tab-content">
                          <TruncatedTabLabel label={displayLabel} tabId={tab.id} />
                          <button
                            className="nav-header__tab-close"
                            type="button"
                            aria-label={t('nav.closeTab')}
                            onClick={e => handleTabClose(e, tab.id)}
                          >
                            <svg viewBox="5 5 14 14" width="7" height="7" xmlns="http://www.w3.org/2000/svg">
                              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor">
                              </path>
                            </svg>
                          </button>
                        </span>
                      ) : (
                        <TruncatedTabLabel label={displayLabel} tabId={tab.id} />
                      )}
                      <span className="nav-header__tab-indicator" />
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}

          
        </div>

          {/* + button — always sits after the last tab */}
        <button
          ref={addBtnRef}
          className={
            'nav-header__tab-add' + (addMenuOpen ? ' nav-header__tab-add--open' : '')
          }
          type="button"
          aria-label={t('nav.openAddMenu')}
          aria-expanded={addMenuOpen}
          onClick={() => (addMenuOpen ? closeAddMenu() : openAddMenu())}
        >
          <span className="material-symbols-outlined">add</span>
        </button>

      </div>

      {/* Right: utility row */}
      <div className="nav-header__right">
        {activeTab?.tabKind === 'workspaces' && (
          <>
            <button
              className={
                'nav-header__icon-btn' +
                (milestonePanelOpen ? ' nav-header__icon-btn--active' : '')
              }
              type="button"
              // title="Milestones"
              aria-label="Toggle milestones panel"
              data-utility="Milestones"
              data-tour-id="workspace-tools-panels"
              data-target="nav-milestones-tooltip"
              aria-pressed={milestonePanelOpen}
              disabled={!onToggleMilestone}
              onClick={onToggleMilestone ?? undefined}
            >
              <span className="material-symbols-outlined">checklist</span>
            </button>
            <Tooltip
              tooltipTarget="nav-milestones-tooltip"
              tooltipText="Milestones"
              arrow={false}
              arrowPosition="Bottom"
            />
          </>
        )}
        {showAssetsButton && (
          <>
            <button
              className={
                'nav-header__icon-btn' +
                (assetsPanelOpen ? ' nav-header__icon-btn--active' : '')
              }
              type="button"
              // title="Assets"
              aria-label="Toggle assets panel"
              data-utility="Assets"
              data-target="nav-assets-tooltip"
              aria-pressed={assetsPanelOpen}
              disabled={!onToggleAssets}
              onClick={onToggleAssets ?? undefined}
            >
              <span className="material-symbols-outlined">inventory_2</span>
            </button>
            <Tooltip
              tooltipTarget="nav-assets-tooltip"
              tooltipText="Assets"
              arrow={false}
              arrowPosition="Bottom"
            />
          </>
        )}
        {activeTab?.tabKind !== 'workspaces' && (
          <>
            <button
              className={
                'nav-header__icon-btn' +
                (activeUtilityTab === 'context' ? ' nav-header__icon-btn--active' : '')
              }
              type="button"
              data-tour-id="workspace-tools-panels"
              data-utility="Context"
              data-target="nav-context-tooltip"
              aria-pressed={activeUtilityTab === 'context'}
              onClick={() => handleUtilityClick('context')}
            >
              <span className="material-symbols-outlined">category</span>
            </button>
            <Tooltip
              tooltipTarget="nav-context-tooltip"
              tooltipText={t('eng.context')}
              arrow={false}
              arrowPosition="Bottom"
            />
          </>
        )}
         {!hideAiSpark && (
          <>
            <button
              className={
                'nav-header__ai-spark' + (aiPanelOpen ? ' nav-header__ai-spark--on' : '')
              }
              type="button"
              // title="Ask KCi"
              aria-label="Open Ask KCi & Team panel"
              data-target="nav-ask-kci-tooltip"
              aria-expanded={aiPanelOpen}
              onClick={aiPanelOpen ? (onCloseAi ?? onToggleAi) : onToggleAi}
            >
              <svg
                className="nav-header__ai-spark-svg"
                width="20"
                height="20"
                viewBox="6 6 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M16.0098 6.00098C16.0098 11.5202 20.4812 15.9944 25.999 16V16.0098C20.4846 16.0154 16.0154 20.4846 16.0098 25.999H16.001C15.9953 20.4812 11.5202 16.0098 6.00098 16.0098H6V16C11.5227 16 16.0009 11.5236 16.001 6.00098V6H16.0098V6.00098Z" />
              </svg>
            </button>
            <Tooltip
              tooltipTarget="nav-ask-kci-tooltip"
              tooltipText="Ask KCi"
              arrow={false}
              arrowPosition="Bottom"
            />
          </>
        )}
        {activeTabId.startsWith('ai-chat-') && (
          <button
            className="nav-header__icon-btn"
            type="button"
            aria-label="New Chat"
            title="New Chat"
            onClick={() => window.dispatchEvent(new CustomEvent('kci:requestNewChat'))}
          >
            <svg width="20" height="20" aria-hidden="true">
              <use href="#cai-home-newchat"></use>
            </svg>
          </button>
        )}
        {activeTabId.startsWith('ai-chat-') && (
          <button
            className="nav-header__icon-btn"
            type="button"
            aria-label="Exit fullscreen Ask KCi"
            title="Exit fullscreen"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent('kci:restoreAiPanel', {
                  detail: { tabId: activeTabId },
                }),
              );
            }}
          >
            <span className="material-symbols-outlined">fullscreen_exit</span>
          </button>
        )}
      </div>

      <TabAddMenu
        tourId="new-recent-tabs"
        open={addMenuOpen}
        onClose={closeAddMenu}
        onAction={handleAddAction}
        triggerRef={addBtnRef}
        position={addMenuPos}
        recentTabs={recentTabs}
        onRecentTabSelect={handleRecentTabSelect}
        onRecentTabPinToggle={handleRecentTabPinToggle}
        onRecentTabAction={onRecentTabAction}
        searchShareRecipients={searchShareRecipients}
        loadSharedRecipientIds={loadSharedRecipientIds}
        allowAgentSharing={allowAgentSharing}
      />
    </nav>
  );
});

export default NavHeader;